import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  MapPin, Calendar, Users, Star, ArrowLeft, 
  Coffee, Wifi, UtensilsCrossed, Car, Waves as Pool, Flower2, 
  Phone, Mail, Globe, Clock, ChevronRight
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { venues } from '../data/venues';
import type { Venue } from '../types/venue';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { SimilarVenueCard } from '../components/SimilarVenueCard';
import { VenueFactbox } from '../components/VenueFactbox';

// Map of amenity icons
const amenityIcons = {
  wifi: Wifi,
  coffee: Coffee,
  restaurant: UtensilsCrossed,
  parking: Car,
  pool: Pool,
  spa: Flower2
};

export const VenuePage: React.FC = () => {
  const { venueId } = useParams<{ venueId: string }>();
  const { t, i18n } = useTranslation();
  const [venue, setVenue] = useState<Venue | null>(null);
  const [similarVenues, setSimilarVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(true);
  
  const lang = i18n.language || 'en';
  
  // Define handlers for Header component
  const handleMenuClick = () => {
    // Handle menu click
  };
  
  const handleAboutClick = () => {
    // Handle about click
  };
  
  const handleContactClick = () => {
    // Handle contact click
  };
  
  useEffect(() => {
    // Scroll to top when page loads
    window.scrollTo(0, 0);
    
    // Find venue by ID
    const foundVenue = venues.find(v => v.id === venueId);
    
    if (foundVenue) {
      setVenue(foundVenue);
      
      // Find similar venues (same region, similar capacity)
      const similar = venues
        .filter(v => 
          v.id !== venueId && 
          (v.region === foundVenue.region || v.country === foundVenue.country) &&
          Math.abs(v.capacity.max - foundVenue.capacity.max) < 100
        )
        .slice(0, 3);
      
      setSimilarVenues(similar);
    }
    
    setLoading(false);
  }, [venueId]);
  
  // Handle case where venue isn't found
  if (!loading && !venue) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header 
          onMenuClick={handleMenuClick}
          onAboutClick={handleAboutClick}
          onContactClick={handleContactClick}
        />
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">
            {t('venues.notFound')}
          </h1>
          <p className="text-gray-600 mb-8">
            {t('venues.venueNotFoundMessage')}
          </p>
          <Link 
            to="/"
            className="inline-flex items-center text-[#2D2F4E] font-medium hover:underline"
          >
            <ArrowLeft size={16} className="mr-2" />
            {t('venues.backToSearch')}
          </Link>
        </div>
      </div>
    );
  }
  
  if (loading || !venue) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-[#2D2F4E] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{venue.name} | {t('common.appName')}</title>
        <meta 
          name="description" 
          content={venue.description[lang]?.replace(/<[^>]*>?/gm, '').substring(0, 160) || ''}
        />
        <meta property="og:title" content={`${venue.name} | ${t('common.appName')}`} />
        <meta 
          property="og:description" 
          content={venue.description[lang]?.replace(/<[^>]*>?/gm, '').substring(0, 160) || ''}
        />
        <meta property="og:image" content={venue.image || venue.images?.[0] || ''} />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={window.location.href} />
      </Helmet>
    
      <Header 
        onMenuClick={handleMenuClick}
        onAboutClick={handleAboutClick}
        onContactClick={handleContactClick}
      />
      
      <div className="container mx-auto px-4 py-6">
        {/* Breadcrumbs */}
        <div className="mb-6">
          <nav className="flex text-sm">
            <Link to="/" className="text-gray-500 hover:text-[#2D2F4E]">
              {t('common.home')}
            </Link>
            <ChevronRight size={16} className="mx-2 text-gray-400 self-center" />
            <Link to="/" className="text-gray-500 hover:text-[#2D2F4E]">
              {t('venues.venues')}
            </Link>
            <ChevronRight size={16} className="mx-2 text-gray-400 self-center" />
            <Link to={`/regions/${venue.region}`} className="text-gray-500 hover:text-[#2D2F4E]">
              {t(`regions.${venue.region}`)}
            </Link>
            <ChevronRight size={16} className="mx-2 text-gray-400 self-center" />
            <span className="text-[#2D2F4E] font-medium">{venue.name}</span>
          </nav>
        </div>
        
        {/* Hero Section */}
        <div className="relative rounded-xl overflow-hidden mb-8 shadow-lg">
          <div className="h-[40vh] md:h-[50vh] w-full relative">
            <img 
              src={venue.image || venue.images?.[0]} 
              alt={venue.name} 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
          </div>
          
          <div className="absolute bottom-0 left-0 w-full p-6 text-white">
            <div className="flex items-center space-x-2 mb-2">
              {venue.recommended && (
                <span className="bg-[#fabddf] text-[#2D2F4E] px-3 py-0.5 rounded-full text-xs font-semibold">
                  ✨ {t('venues.recommended')}
                </span>
              )}
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, index) => (
                  <Star 
                    key={index}
                    className={`w-5 h-5 ${index < Math.floor(venue.rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
                  />
                ))}
                <span className="ml-2 text-lg font-medium">{venue.rating.toFixed(1)}</span>
              </div>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2">{venue.name}</h1>
            <div className="flex items-center text-sm">
              <MapPin size={16} className="mr-1" />
              <span>{venue.city}, {t(`regions.${venue.region}`)}</span>
            </div>
          </div>
        </div>
        
        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Quick Info */}
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div className="p-3">
                  <Users className="w-6 h-6 mx-auto mb-2 text-[#2D2F4E]" />
                  <div className="text-sm text-gray-500">{t('venues.capacity')}</div>
                  <div className="font-semibold text-gray-700">
                    {venue.capacity.min} - {venue.capacity.max}
                  </div>
                </div>
                
                <div className="p-3">
                  <Star className="w-6 h-6 mx-auto mb-2 text-[#2D2F4E]" />
                  <div className="text-sm text-gray-500">{t('venues.rating')}</div>
                  <div className="font-semibold text-gray-700">{venue.rating}/5</div>
                </div>
                
                <div className="p-3">
                  <MapPin className="w-6 h-6 mx-auto mb-2 text-[#2D2F4E]" />
                  <div className="text-sm text-gray-500">{t('venues.country')}</div>
                  <div className="font-semibold text-gray-700">
                    {t(`venues.countries.${venue.country}`)}
                  </div>
                </div>
                
                <div className="p-3">
                  <Calendar className="w-6 h-6 mx-auto mb-2 text-[#2D2F4E]" />
                  <div className="text-sm text-gray-500">{t('venues.bookings')}</div>
                  <div className="font-semibold text-gray-700">{t('venues.yearRound')}</div>
                </div>
              </div>
            </div>
            
            {/* Description */}
            <div className="bg-white p-6 rounded-xl shadow-sm mb-6">
              <h3 className="font-medium text-gray-700 mb-3">
                {t('venues.description')}
              </h3>
              <div 
                className="venue-description"
                dangerouslySetInnerHTML={{ __html: venue.description[lang] || venue.description.en }}
              ></div>
            </div>
            
            {/* Add factbox here */}
            <div className="bg-white p-6 rounded-xl shadow-sm mb-6">
              <VenueFactbox venue={venue} />
            </div>
            
            {/* Amenities */}
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">{t('venues.amenities')}</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {venue.amenities.map(amenity => {
                  const Icon = amenityIcons[amenity as keyof typeof amenityIcons];
                  return (
                    <div key={amenity} className="flex items-center">
                      {Icon && (
                        <div className="w-8 h-8 rounded-full bg-[#fabddf]/10 flex items-center justify-center mr-3">
                          <Icon size={18} className="text-[#2D2F4E]" />
                        </div>
                      )}
                      <span className="text-gray-700">{t(`venues.amenities.${amenity}`)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Location */}
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">{t('venues.location')}</h2>
              <p className="mb-4 text-gray-700">
                <MapPin size={16} className="inline mr-2 text-[#2D2F4E]" /> 
                {venue.address}
              </p>
              <div className="h-64 rounded-lg overflow-hidden bg-gray-200">
                <iframe 
                  title={`Map of ${venue.name}`}
                  width="100%" 
                  height="100%" 
                  frameBorder="0" 
                  src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBGE9hHQH62PH9Q-7s-Yef-sQhBfWgBYOE&q=${encodeURIComponent(venue.address)}`} 
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          </div>
          
          {/* Right Column - Booking & Related Info */}
          <div className="space-y-6">
            {/* Booking Card */}
            <div className="bg-white p-6 rounded-xl shadow-sm sticky top-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                {t('venues.bookThis')}
              </h2>
              
              <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-700 mb-1">
                  {t('venues.priceRange')}
                </h3>
                <div className="text-2xl font-bold text-[#2D2F4E]">
                  {venue.priceRange.min.toLocaleString()} - {venue.priceRange.max.toLocaleString()} SEK
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {t('venues.priceDescription')}
                </p>
              </div>
              
              <button className="w-full bg-[#2D2F4E] text-white py-3 rounded-lg hover:bg-[#232445] transition-colors font-medium mb-3">
                {t('venues.requestQuote')}
              </button>
              
              <button className="w-full border border-[#2D2F4E] text-[#2D2F4E] py-3 rounded-lg hover:bg-[#2D2F4E]/5 transition-colors font-medium">
                {t('venues.checkAvailability')}
              </button>
              
              <div className="mt-6 border-t pt-4">
                <div className="flex items-center mb-2">
                  <Clock size={16} className="text-gray-500 mr-2" />
                  <span className="text-sm text-gray-700">{t('venues.quickResponse')}</span>
                </div>
                <div className="flex items-center mb-2">
                  <Phone size={16} className="text-gray-500 mr-2" />
                  <a href="tel:+46123456789" className="text-sm text-gray-700 hover:underline">
                    +46 123 456 789
                  </a>
                </div>
                <div className="flex items-center">
                  <Mail size={16} className="text-gray-500 mr-2" />
                  <a href="mailto:booking@venue.com" className="text-sm text-gray-700 hover:underline">
                    booking@venue.com
                  </a>
                </div>
              </div>
            </div>
            
            {/* Website + Social */}
            {venue.website && (
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <h3 className="font-medium text-gray-700 mb-3">
                  {t('venues.officialWebsite')}
                </h3>
                <a 
                  href={venue.website} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center text-[#2D2F4E] hover:underline"
                >
                  <Globe size={16} className="mr-2" />
                  {venue.website.replace(/^https?:\/\/(www\.)?/, '')}
                </a>
              </div>
            )}
          </div>
        </div>
        
        {/* Similar Venues */}
        {similarVenues.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">
              {t('venues.similarVenues')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {similarVenues.map(similarVenue => (
                <SimilarVenueCard key={similarVenue.id} venue={similarVenue} />
              ))}
            </div>
          </div>
        )}
      </div>
      
      <Footer />
    </div>
  );
}; 